import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Chip } from '@mui/material';

import useCar from '~/data/hooks/useCar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';

type PropertyTicketScopeChipProps = {
  carId: string;
  onClear: () => void;
};

/**
 * Which elevator a KPI link narrowed the list to. The fetch reads `car_id`, but no column
 * shows it, so without this the reader gets a short list and nothing saying why. Shaped like
 * the global filter bar's chips, the app's other "what this page is scoped to" chip.
 */
export function PropertyTicketScopeChip({ carId, onClear }: PropertyTicketScopeChipProps) {
  const terms = useLocaleTerms();
  const { car } = useCar(carId);

  // Shown whether or not the name arrives: the list is narrowed either way, and a lookup that
  // fails — a deleted car, one the reader cannot see — would otherwise leave nothing on screen
  // to say so, or to undo it.
  const name = car?.name;
  const unit = terms.unit.toLowerCase();
  const scope = name ? `${terms.unit} ${name}` : `one ${unit}`;

  return (
    <Chip
      size="small"
      // The chip is the button; MUI hides the delete icon from assistive tech, so its own
      // label never reached anyone. Delete and Backspace are the keys that remove it.
      aria-label={`Showing tickets for ${scope} only. Press Delete to show all ${terms.units.toLowerCase()}.`}
      label={
        name ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
            <Box
              component="span"
              sx={{ fontSize: '10px', fontWeight: 400, color: 'text.secondary' }}
            >
              {terms.unit}
            </Box>{' '}
            <Box component="span">{name}</Box>
          </Box>
        ) : (
          `One ${unit}`
        )
      }
      onDelete={onClear}
      deleteIcon={<CancelIcon />}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.main',
        color: 'primary.main',
      }}
    />
  );
}
